import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "cleaner" ? "/cleaner" : "/owner");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/28586197/pexels-photo-28586197.jpeg')",
          }}
        />
        <div className="relative z-10 flex items-center gap-3">
          <div className="bg-white rounded-xl p-1.5">
            <img src="/logo.png" alt="SmartHost Solutions" className="h-9 w-9 object-contain" />
          </div>
          <span className="font-display text-2xl font-bold">SmartHost Solutions</span>
        </div>
        <div className="relative z-10 space-y-4">
          <h1 className="font-display text-4xl xl:text-5xl font-semibold leading-tight tracking-tight">
            Short Term Management &amp; Turnover Services
          </h1>
          <p className="text-base text-primary-foreground/80 max-w-md leading-relaxed">
            Smart Systems. Seamless Stays.
          </p>
        </div>
        <div className="relative z-10 text-sm text-primary-foreground/70">
          © {new Date().getFullYear()} SmartHost Solutions
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/logo.png" alt="SmartHost Solutions" className="h-10 w-10 object-contain" />
            <span className="font-display text-xl font-bold">SmartHost Solutions</span>
          </div>
          <h2 className="font-display text-2xl font-semibold mb-1">Welcome back</h2>
          <p className="text-muted-foreground text-sm mb-8">
            Sign in to your SmartHost account
          </p>
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <p className="text-sm text-destructive" data-testid="login-error">
                {error}
              </p>
            )}
            <Button
              type="submit"
              className="w-full h-11 text-base"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign in
            </Button>
            <Link to="/forgot-password" className="block text-center text-sm text-primary mt-4 hover:underline" data-testid="forgot-password-link">
              Forgot your password?
            </Link>
          </form>
          <div className="mt-8 rounded-xl bg-secondary p-4 text-sm text-secondary-foreground">
            <p className="font-semibold mb-1">Demo accounts</p>
            <p>Owner: owner@cleancrew.com / owner123</p>
            <p className="text-muted-foreground text-xs mt-1">
              Cleaners are created by the owner inside the dashboard.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
